import React from 'react';
import { formatHours, formatPremiumHours } from '../../utils/formatHours';
import { Drawer } from '@mui/material';
import {
  addCalendarDays,
  parseLocalCalendarDate,
} from '../../utils/field-start-date-policy';

/**
 * PackageLaborDrawer
 * Right-side MUI Drawer — chart view only.
 * Shows all-time budget summary + employee-level daily breakdown for a selected package.
 * Props: open, pkg, drawerDetail, drawerLoading, weekLabel, onClose
 */
const PackageLaborDrawer = ({
  open,
  pkg,
  drawerDetail,
  drawerLoading,
  weekLabel,
  onClose,
}) => {
  if (!pkg) return null;

  // ── Budget % helpers ──────────────────────────────────────────────────────
  const fieldPct =
    pkg.field_hrs_budgeted > 0
      ? Math.round((pkg.field_hrs_alltime / pkg.field_hrs_budgeted) * 100)
      : null;
  const shopPct =
    pkg.shop_hrs_budgeted > 0
      ? Math.round((pkg.shop_hrs_alltime / pkg.shop_hrs_budgeted) * 100)
      : null;
  const combinedAlltime =
    (pkg.field_hrs_alltime || 0) + (pkg.shop_hrs_alltime || 0);
  const combinedBudget =
    (pkg.field_hrs_budgeted || 0) + (pkg.shop_hrs_budgeted || 0);
  const combinedPct =
    combinedBudget > 0
      ? Math.round((combinedAlltime / combinedBudget) * 100)
      : null;

  const pctColor = (pct) => {
    if (pct === null) return '#64748b';
    if (pct >= 100) return '#dc2626';
    if (pct >= 85) return '#d97706';
    return '#15803d';
  };

  // ── Day card from daily array ─────────────────────────────────────────────
  const getDayHours = (emp, dateStr) => {
    const entries = (emp.daily || []).filter((d) => d.date === dateStr);
    const totalSt = entries.reduce((s, e) => s + (e.hours_st || 0), 0);
    const totalOt = entries.reduce((s, e) => s + (e.hours_ot || 0), 0);
    const totalDt = entries.reduce((s, e) => s + (e.hours_dt || 0), 0);
    return {
      total: totalSt + totalOt + totalDt,
      st: totalSt,
      ot: totalOt,
      dt: totalDt,
    };
  };

  // Build Mon–Sun dates from drawerDetail week_start
  const weekDays = [];
  if (drawerDetail?.week_start) {
    for (let i = 0; i < 7; i++) {
      const date = addCalendarDays(drawerDetail.week_start, i);
      weekDays.push({
        date,
        label: parseLocalCalendarDate(date).toLocaleDateString('en-US', {
          weekday: 'short',
        }),
      });
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 420, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          padding: '16px 20px',
          backgroundColor: '#1e293b',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
            {[
              pkg.description ? pkg.autodesk_name : null,
              pkg.iwp,
              pkg.area_name,
              pkg.pm_name,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.3,
              maxWidth: 330,
              wordBreak: 'break-word',
            }}
          >
            {pkg.description || pkg.autodesk_name || 'Unnamed Package'}
          </div>
          {pkg.tracking_status_label && (
            <span
              style={{
                display: 'inline-block',
                marginTop: 6,
                fontSize: 11,
                fontWeight: 600,
                color: pkg.tracking_status_color || '#64748b',
                border: `1px solid ${pkg.tracking_status_color || '#64748b'}`,
                borderRadius: 20,
                padding: '2px 8px',
              }}
            >
              {pkg.tracking_status_label}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            fontSize: 22,
            cursor: 'pointer',
            lineHeight: 1,
            marginLeft: 8,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* ── All-Time Combined section ── */}
      {combinedBudget > 0 && (
        <div
          style={{
            padding: '12px 20px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: '#94a3b8',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            All-Time Combined
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: pctColor(combinedPct),
              }}
            >
              {formatHours(combinedAlltime)}
            </span>
            {combinedPct !== null && (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: pctColor(combinedPct),
                  backgroundColor: pctColor(combinedPct) + '15',
                  padding: '3px 9px',
                  borderRadius: 10,
                  border: `1px solid ${pctColor(combinedPct)}33`,
                }}
              >
                {combinedPct}%
              </span>
            )}
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              of {formatHours(combinedBudget)} budget
            </span>
          </div>
        </div>
      )}

      {/* ── Field / Shop split ── */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}
      >
        {[
          [
            'Field',
            pkg.field_hrs_alltime,
            pkg.field_hrs_budgeted,
            fieldPct,
            '#3b82f6',
          ],
          [
            'Shop',
            pkg.shop_hrs_alltime,
            pkg.shop_hrs_budgeted,
            shopPct,
            '#8b5cf6',
          ],
        ].map(([label, actual, budget, pct, color]) => (
          <div
            key={label}
            style={{
              flex: 1,
              padding: '12px 16px',
              textAlign: 'center',
              borderRight: label === 'Field' ? '1px solid #e2e8f0' : 'none',
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: budget && actual > budget ? '#dc2626' : color,
              }}
            >
              {formatHours(actual || 0)}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
              {label}{' '}
              {budget ? `/ ${formatHours(budget)} budget` : '(no budget)'}
            </div>
            {pct !== null && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: pctColor(pct),
                  marginTop: 4,
                }}
              >
                {pct}% used
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Employee section ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div
          style={{
            padding: '10px 20px 4px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#94a3b8',
            textTransform: 'uppercase',
          }}
        >
          Employees — {weekLabel}
        </div>

        {drawerLoading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
            Loading...
          </div>
        ) : (drawerDetail?.employees || []).length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: 13,
            }}
          >
            No timecards for this week
          </div>
        ) : (
          (drawerDetail.employees || []).map((emp) => (
            <div
              key={emp.employee_id}
              style={{
                borderBottom: '1px solid #f1f5f9',
                padding: '12px 20px',
              }}
            >
              {/* Employee name + totals */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 4,
                }}
              >
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}
                >
                  {emp.firstname} {emp.lastname}
                </span>
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}
                >
                  {formatHours(
                    (emp.field_hrs_total || 0) + (emp.shop_hrs_total || 0)
                  )}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                Field: {formatHours(emp.field_hrs_total || 0)} · Shop:{' '}
                {formatHours(emp.shop_hrs_total || 0)}
              </div>

              {/* Daily breakdown */}
              {weekDays.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      color: '#cbd5e1',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    Daily Breakdown
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {weekDays.map((day) => {
                      const { total, st, ot, dt } = getDayHours(emp, day.date);
                      if (total === 0) return null;
                      return (
                        <div
                          key={day.date}
                          style={{
                            backgroundColor: '#f1f5f9',
                            borderRadius: 6,
                            padding: '5px 8px',
                            textAlign: 'center',
                            minWidth: 42,
                          }}
                        >
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>
                            {day.label}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#1e293b',
                            }}
                          >
                            {formatHours(st)}
                          </div>
                          {ot > 0 && (
                            <div
                              style={{
                                fontSize: 10,
                                color: '#f59e0b',
                                fontWeight: 600,
                              }}
                            >
                              {formatPremiumHours(ot, 'OT')}
                            </div>
                          )}
                          {dt > 0 && (
                            <div
                              style={{
                                fontSize: 10,
                                color: '#ef4444',
                                fontWeight: 600,
                              }}
                            >
                              {formatPremiumHours(dt, 'DT')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </Drawer>
  );
};

export default PackageLaborDrawer;
